import { NumberFilter } from "@react3l/advanced-filters";
import { AxisResponse, Dashboard, DashboardFilter } from "models/Dashboard";
import React, { CSSProperties, PropsWithChildren } from "react";
import Chart from "react-apexcharts";
import { Observable } from "rxjs";

type SeriesItem = { name?: string; data: number[] };

// Màu chuỗi theo thứ tự cố định, cùng họ brand (xanh lá, xanh navy nâng sáng). Đã qua validate_palette (skill dataviz):
// navy gốc #001c4e quá tối và navy 70% gần xám nên không dùng cho dữ liệu. Không dùng đỏ: dễ đọc thành lỗi.
// #5cbf55 tương phản thấp với nền trắng: biểu đồ luôn kèm chú thích và tooltip.
export const CHART_COLORS = ["#209b24", "#2b5cad", "#5cbf55", "#3f8fd0"];
const CHART_TEXT_COLOR = "#4d6083"; // --muted
const CHART_GRID_COLOR = "#e8ebf0"; // --border

const numberFormat = new Intl.NumberFormat("vi-VN");
function formatNumber(value: number | string) {
  return typeof value === "number" ? numberFormat.format(value) : `${value}`;
}

export const BarChart = React.memo((props: PropsWithChildren<Props>) => {
  const { loadData, filter, height, horizontal, stacked, colors, type = "bar" } = props;

  const [data, setData] = React.useState<AxisResponse>();

  const series = React.useMemo((): SeriesItem[] => {
    const incoming = data?.series;
    if (Array.isArray(incoming) && incoming.length > 0) {
      // Filter bỏ series "Vấn đề"
      return (incoming as SeriesItem[]).filter(
        (item) => item?.name !== "Vấn đề"
      );
    }
    return [{ name: "", data: [] }];
  }, [data]);

  const options = {
    chart: {
      stacked: stacked ?? false,
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
      foreColor: CHART_TEXT_COLOR,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: horizontal ?? false,
        columnWidth: "56%",
        barHeight: "56%",
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: colors ?? CHART_COLORS,
    stroke:
      type === "line"
        ? { show: true, width: 2, curve: "smooth" as const }
        : { show: true, width: 2, colors: ["transparent"] },
    markers: { size: 0, hover: { size: 5 } },
    grid: {
      borderColor: CHART_GRID_COLOR,
      strokeDashArray: 0,
      xaxis: { lines: { show: horizontal ?? false } },
      yaxis: { lines: { show: !horizontal } },
    },
    legend: {
      position: "top" as const,
      horizontalAlign: "left" as const,
      fontSize: "12px",
      markers: { size: 4 },
      itemMargin: { horizontal: 12 },
    },
    xaxis: {
      categories: data?.horizontalAxis ?? [],
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickAmount: horizontal ? 4 : undefined, // trục số nằm ngang: ít mốc để màn hẹp không đè chữ
      labels: { formatter: horizontal ? formatNumber : undefined },
    },
    yaxis: {
      title: {},
      labels: { formatter: horizontal ? undefined : formatNumber },
    },
    fill: { opacity: 1 },
    tooltip: {
      y: {
        formatter: formatNumber,
      },
    },
  };

  React.useEffect(() => {
    const subscription = loadData({
      time: new NumberFilter({ equal: filter.time }),
      type: new NumberFilter({ equal: filter.type }),
      skip: 0,
      take: 1,
    }).subscribe({
      next: (response: AxisResponse) => {
        setData(response);
      },
      error: () => {},
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [filter, loadData]);

  const isEmpty = data !== undefined && series.every((item) => !item.data?.some((value) => Number(value) > 0));
  if (isEmpty) {
    return (
      <div className="chart-empty" style={{ height: Math.min(height ?? 160, 160) }}>
        Chưa có dữ liệu trong kỳ này
      </div>
    );
  }

  return <Chart series={series} options={options} type={type} height={height} />;
});

export interface Props {
  filter: Dashboard;
  loadData: (filter: DashboardFilter) => Observable<any>;
  style?: CSSProperties;
  stacked?: boolean;
  horizontal?: boolean;
  height?: number;
  colors?: string[];
  type?: "bar" | "line";
}
